import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lightbulb, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function TreatmentInsights() {
  const [cases, setCases] = useState([]);
  const [caseId, setCaseId] = useState("");
  const [insight, setInsight] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/cases").then((r) => setCases(r.data)); }, []);

  const generate = async () => {
    if (!caseId) return toast.error("Select a case");
    const c = cases.find((x) => x.id === caseId);
    setBusy(true); setInsight("");
    try {
      const { data } = await api.post("/ai/insight", {
        prompt: "Provide a concise treatment insight and any clinical considerations for this implant case, including risk factors and recommended approach.",
        context: { patient: c.patient_name, treatment: c.treatment, diagnosis: c.diagnosis, findings: c.findings, implant_site: c.implant_site },
      });
      setInsight(data.insight);
    } catch { toast.error("AI unavailable"); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Treatment Insights" subtitle="AI-assisted clinical considerations for your cases" breadcrumb={["Intelligence", "Treatment Insights"]} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SurfaceCard className="p-5 h-fit">
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Case</Label>
              <Select value={caseId} onValueChange={setCaseId}>
                <SelectTrigger className="mt-1.5" data-testid="insight-case-select"><SelectValue placeholder="Select case" /></SelectTrigger>
                <SelectContent>{cases.map((c) => <SelectItem key={c.id} value={c.id}>{c.patient_name} · {c.case_id}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={generate} disabled={busy} data-testid="insight-generate-btn" className="w-full bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">
              {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Analyzing...</> : <><Sparkles className="h-4 w-4 mr-1.5" />Get Insight</>}
            </Button>
          </div>
        </SurfaceCard>

        <div className="lg:col-span-2">
          <SurfaceCard dark className="p-6 min-h-[240px]">
            <div className="flex items-center gap-2 mb-4"><Lightbulb className="h-4 w-4 text-[#3FD98A]" /><span className="text-sm font-bold text-white">Treatment Insight</span></div>
            {busy ? <div className="flex items-center gap-2 text-sm text-[#8B9698]"><Loader2 className="h-4 w-4 animate-spin" />CONTAHA AI is analyzing the case...</div> :
              insight ? <p className="text-sm leading-relaxed text-white/90 whitespace-pre-wrap">{insight}</p> :
              <div className="flex flex-col items-center justify-center py-10 text-center"><Lightbulb className="h-8 w-8 text-[#4E5F68] mb-2" /><p className="text-sm text-[#8B9698]">Select a case and generate an AI insight.</p></div>}
            {insight && <div className="mt-4 text-[10px] text-[#4E5F68]">AI assists. Clinician decides. Always verify against clinical judgment.</div>}
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}
